import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { CreateSleepScreen } from '../../libs/GraphQL';
import SleepScreenVideoPreview from './SleepScreenVideoPreview';
import styles from './CreateSleepScreenModal.module.css';

const CLOUDINARY_PREFIX = 'https://res.cloudinary.com/fftech/video';

const deriveSleepScreenId = (displayName) => {
  if (!displayName) return '';
  return displayName
    .replace(/['’]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
};

const CreateSleepScreenModal = ({ show, onCancel, onCreated }) => {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const resetState = () => {
    setDisplayName('');
    setDescription('');
    setVideoUrl('');
    setFieldErrors({});
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    resetState();
    onCancel();
  };

  const validate = (derivedId) => {
    const errors = {};
    if (!displayName.trim()) {
      errors.displayName = 'Display name is required.';
    } else if (!derivedId) {
      errors.displayName =
        'Display name must include letters or numbers to generate an ID.';
    }
    if (!videoUrl.trim()) {
      errors.videoUrl = 'Video URL is required.';
    } else if (!videoUrl.trim().startsWith(CLOUDINARY_PREFIX)) {
      errors.videoUrl = `Must be a Cloudinary video URL (starts with ${CLOUDINARY_PREFIX}).`;
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sleepScreenId = deriveSleepScreenId(displayName);
    const errors = validate(sleepScreenId);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const input = {
        sleepScreenId,
        displayName: displayName.trim(),
        videoUrl: videoUrl.trim(),
      };
      const trimmedDescription = description.trim();
      if (trimmedDescription) {
        input.description = trimmedDescription;
      }
      const userEmail = localStorage.getItem('userEmail');
      if (userEmail) {
        input.createdBy = userEmail;
      }
      const entry = await CreateSleepScreen(input);
      resetState();
      onCreated(entry);
    } catch (err) {
      // Amplify wraps GraphQL errors as { errors: [{ errorType, message }] };
      // CreateSleepScreen helper throws the inner error directly. Handle both.
      const firstError = (err && err.errors && err.errors[0]) || err;
      const errorType = firstError && firstError.errorType;
      if (
        errorType === 'SLEEP_SCREEN_ID_ALREADY_EXISTS' ||
        errorType === 'INVALID_SLEEP_SCREEN_ID'
      ) {
        setFieldErrors({
          displayName:
            errorType === 'SLEEP_SCREEN_ID_ALREADY_EXISTS'
              ? 'An entry with this name already exists. Pick a different display name.'
              : "This display name can't be used. Try adding or changing some letters or numbers.",
        });
      } else if (errorType === 'INVALID_CLOUDINARY_URL') {
        setFieldErrors({
          videoUrl:
            (firstError && firstError.message) || 'Invalid Cloudinary URL.',
        });
      } else {
        toast.error('Something went wrong, please try again.');
        console.error('createSleepScreen failed:', err);
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>New library entry</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className={styles.form}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="ssDisplayName">
                Display Name<span className={styles.required}>*</span>
              </label>
              <input
                id="ssDisplayName"
                type="text"
                className={`${styles.input} ${
                  fieldErrors.displayName ? styles.inputError : ''
                }`}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={submitting}
              />
              <span className={styles.hint}>
                how marketing will refer to this screen
              </span>
              {fieldErrors.displayName && (
                <span className={styles.fieldError}>
                  {fieldErrors.displayName}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="ssDescription">
                Description
              </label>
              <textarea
                id="ssDescription"
                className={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submitting}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label} htmlFor="ssVideoUrl">
                Video URL<span className={styles.required}>*</span>
              </label>
              <input
                id="ssVideoUrl"
                type="text"
                className={`${styles.input} ${
                  fieldErrors.videoUrl ? styles.inputError : ''
                }`}
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder={CLOUDINARY_PREFIX}
                disabled={submitting}
              />
              {fieldErrors.videoUrl && (
                <span className={styles.fieldError}>
                  {fieldErrors.videoUrl}
                </span>
              )}
              {videoUrl.trim().startsWith(CLOUDINARY_PREFIX) && (
                <div className={styles.previewWrapper}>
                  <SleepScreenVideoPreview url={videoUrl.trim()} />
                </div>
              )}
            </div>

            <div className={styles.nextNote}>
              <strong>next →</strong>
              after create, you&apos;ll be asked whether to assign this entry to
              fridges now.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className={styles.footerButtons}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
              disabled={submitting}
            >
              cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={submitting}
            >
              {submitting ? 'creating…' : 'create entry →'}
            </button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

CreateSleepScreenModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};

export default CreateSleepScreenModal;
